import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BiCloudUpload } from "react-icons/bi";
import { FaEdit, FaTrashAlt, FaEye } from "react-icons/fa";

const BannerPage = () => {
  const [banners, setBanners] = useState([]);
  const [images, setImages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedBannerId, setSelectedBannerId] = useState(null);
  const [updateImages, setUpdateImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  // Fetch banners from API
  const fetchBanners = async () => {
    try {
      const response = await axios.get("https://api.elthiumhealth.com/api/admin/getbanners");
      if (response.data && response.data.imageUrls) {
        const formattedBanners = response.data.imageUrls.map((banner) => ({
          _id: banner._id,
          imageUrls: banner.imageUrls.map((imageUrl) => imageUrl.replace(/\\/g, "/")),
        }));
        setBanners(formattedBanners);
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle image change for create
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setImages([...e.target.files]);
    }
  };

  // Handle image change for update
  const handleUpdateImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUpdateImages([...e.target.files]);
    }
  };

  // Handle form submission to upload images
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (images.length === 0) {
      setMessage("At least one banner image is required");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      images.forEach((file) => formData.append("bannerImages", file));

      const response = await axios.post(
        "https://api.elthiumhealth.com/api/admin/uploadbanner",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessage(response.data.message || "Banner created successfully!");
      setImages([]);
      setTimeout(() => {
        fetchBanners();
        setMessage("");
      }, 1500);
    } catch (error) {
      console.error("Error creating banner:", error);
      setMessage("Failed to create banner. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle banner deletion
  const handleDelete = async (bannerId) => {
    if (!bannerId) {
      console.error("Invalid banner ID");
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this banner?");
    if (!confirmed) return;

    try {
      const response = await axios.delete(`https://api.elthiumhealth.com/api/admin/delete-banner/${bannerId}`);
      alert(response.data.message || "Banner deleted successfully!");
      setTimeout(() => {
        fetchBanners();
      }, 1500);
    } catch (error) {
      console.error("Error deleting banner:", error);
      setMessage("Failed to delete banner. Please try again.");
    }
  };

  // Handle banner update with images
  const handleUpdate = async () => {
    if (!selectedBannerId) return;

    setLoading(true);
    try {
      const formData = new FormData();
      if (updateImages.length > 0) {
        updateImages.forEach((file) => formData.append("bannerImages", file));
      }

      const response = await axios.put(
        `https://api.elthiumhealth.com/api/admin/update-banner/${selectedBannerId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert(response.data.message || "Banner updated successfully!");
      setShowUpdateModal(false);
      setUpdateImages([]);
      setSelectedBannerId(null);
      setTimeout(() => {
        fetchBanners();
      }, 1500);
    } catch (error) {
      console.error("Error updating banner:", error);
      setMessage("Failed to update banner. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Open update modal with existing images
  const openUpdateModal = (banner) => {
    setSelectedBannerId(banner._id);
    setExistingImages(banner.imageUrls);
    setUpdateImages([]);
    setShowUpdateModal(true);
  };

  // Remove existing image from update
  const removeExistingImage = (imageToRemove) => {
    setExistingImages(existingImages.filter(img => img !== imageToRemove));
  };

  // Open view modal
  const openViewModal = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  return (
    <div className="flex justify-center items-center my-12 bg-gray-100 min-h-[calc(100vh-100px)] px-4">
      <div className="flex space-x-8 w-full">
        {/* Create Banner Section */}
        <div className="w-1/2 p-6 bg-white rounded shadow">
          <h3 className="text-lg font-bold mb-4 text-[#188753] text-center">Create Banner</h3>
          {message && (
            <div
              className={`mb-4 text-sm text-center font-medium ${
                message.toLowerCase().includes("success") ? "text-green-600" : "text-red-600"
              }`}
            >
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Upload Images</label>
              <div
                className="w-full border border-dashed border-gray-400 p-4 rounded flex flex-col items-center justify-center cursor-pointer hover:border-green-500"
                onClick={() => fileInputRef.current.click()}
              >
                <BiCloudUpload className="text-4xl text-gray-600 mb-2" />
                <p className="text-sm text-gray-600">Click to upload images</p>
                {images.length > 0 && (
                  <p className="mt-2 text-sm text-green-600 font-semibold">
                    {images.length} image(s) selected
                  </p>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                multiple
                className="hidden"
              />
            </div>

            {/* Preview selected images */}
            {images.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">Preview:</label>
                <div className="flex flex-wrap gap-2">
                  {Array.from(images).map((file, index) => (
                    <img
                      key={index}
                      src={URL.createObjectURL(file)}
                      alt={`preview-${index}`}
                      className="w-20 h-20 object-cover rounded border"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={() => setImages([])}
                className="px-4 py-2 text-red-700 bg-red-100 border border-red-600 rounded hover:bg-red-200"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-white bg-[#188753] border border-green-700 rounded hover:bg-green-700"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </div>

        {/* Banner List Section */}
        <div className="w-1/2 p-6 bg-white rounded shadow">
          <h3 className="text-lg font-bold mb-4 text-[#188753] text-center">Banner List</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border-b p-3 text-left">Images</th>
                  <th className="border-b p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {banners.map((banner, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border-b p-3">
                      <div className="flex flex-wrap gap-2">
                        {banner.imageUrls.slice(0, 2).map((url, i) => (
                          <img
                            key={i}
                            src={`https://api.elthiumhealth.com/${url}`}
                            alt={`banner-${i}`}
                            className="w-16 h-16 object-cover rounded border"
                          />
                        ))}
                        {banner.imageUrls.length > 2 && (
                          <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-gray-500">
                            +{banner.imageUrls.length - 2}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="border-b p-3">
                      <div className="flex gap-3">
                        <FaEye
                          className="text-green-500 cursor-pointer text-xl hover:text-green-700 transition-colors"
                          onClick={() => openViewModal(banner)}
                          title="View Banner"
                        />
                        <FaEdit
                          className="text-blue-500 cursor-pointer text-xl hover:text-blue-700 transition-colors"
                          onClick={() => openUpdateModal(banner)}
                          title="Edit Banner"
                        />
                        <FaTrashAlt
                          className="text-red-500 cursor-pointer text-xl hover:text-red-700 transition-colors"
                          onClick={() => handleDelete(banner._id)}
                          title="Delete Banner"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {banners.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No banners found. Create your first banner!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Update Modal */}
      {showUpdateModal && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-1/2 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-[#188753] mb-4">Update Banner Images</h3>
            
            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Current Images:</label>
                <div className="flex flex-wrap gap-3">
                  {existingImages.map((url, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={`https://api.elthiumhealth.com/${url}`}
                        alt={`existing-${idx}`}
                        className="w-24 h-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(url)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                {existingImages.length > 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Note: Removed images will be deleted from the banner
                  </p>
                )}
              </div>
            )}

            {/* Add New Images */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Add New Images:</label>
              <div
                className="w-full border border-dashed border-gray-400 p-4 rounded flex flex-col items-center justify-center cursor-pointer hover:border-green-500"
                onClick={() => document.getElementById("updateFileInput").click()}
              >
                <BiCloudUpload className="text-4xl text-gray-600 mb-2" />
                <p className="text-sm text-gray-600">Click to upload new images</p>
                {updateImages.length > 0 && (
                  <p className="mt-2 text-sm text-green-600 font-semibold">
                    {updateImages.length} new image(s) selected
                  </p>
                )}
              </div>
              <input
                type="file"
                id="updateFileInput"
                onChange={handleUpdateImageChange}
                accept="image/*"
                multiple
                className="hidden"
              />
            </div>

            {/* Preview New Images */}
            {updateImages.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">New Images Preview:</label>
                <div className="flex flex-wrap gap-2">
                  {Array.from(updateImages).map((file, index) => (
                    <img
                      key={index}
                      src={URL.createObjectURL(file)}
                      alt={`new-preview-${index}`}
                      className="w-20 h-20 object-cover rounded border"
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowUpdateModal(false);
                  setUpdateImages([]);
                  setExistingImages([]);
                }}
                className="px-4 py-2 text-red-700 bg-red-100 border border-red-600 rounded hover:bg-red-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdate}
                disabled={loading}
                className="px-4 py-2 text-white bg-[#188753] border border-green-700 rounded hover:bg-green-700"
              >
                {loading ? "Updating..." : "Update Banner"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedBanner && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-2/3 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-[#188753] mb-4 text-center">Banner Images</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {selectedBanner.imageUrls.map((url, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={`https://api.elthiumhealth.com/${url}`}
                    alt={`banner-${idx}`}
                    className="w-full h-48 object-cover rounded-lg shadow-md cursor-pointer"
                    onClick={() => window.open(`https://api.elthiumhealth.com/${url}`, '_blank')}
                  />
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    {idx + 1}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 text-white bg-gray-600 rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerPage;